// Copyright 2020 TOKU Systems Inc.

import React, { useState, useEffect } from 'react';

import {
  Device,
  DeviceModel,
  EntityRef,
  parseEntityRef,
  Signal,
  Hardpoint,
} from '@equestria/spike';
import { IconButton, Grid, Icon } from '@material-ui/core';
import { List, Map, Set } from 'immutable';
import SignalItem from './signal-item';
import {
  SignalCursorValue,
  SignalPlot,
  temporalResolutionForSamplingFrequency,
  colorPalette,
} from './signal-plot';
import illumass from '../../services/illumass';
import { useSnackbar } from 'notistack';
import _ from 'lodash';
import SignalPicker from './signal-picker';
import InfoDialog from '../info-dialog';

interface SignalsBarProps {
  originRefs: List<string | EntityRef>;
  cursorValues?: Map<string, SignalCursorValue>;
  onSignalPlotsChange: (signalPlots: List<SignalPlot>) => void;
}

export default function SignalsBar(props: SignalsBarProps): JSX.Element {
  const [signalPickerOriginId, setSignalPickerOriginId] = useState('');
  const [signalPlots, setSignalPlots] = useState<List<SignalPlot>>(List());
  const [origins, setOrigins] = useState<List<Device | Hardpoint>>(List());
  const [models, setModels] = useState(Map<string, DeviceModel>());
  const [initializationStarted, setInitializationStarted] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { originRefs, onSignalPlotsChange } = props;
  const [infoSignal, setInfoSignal] = useState<Signal | undefined>(undefined);

  const makeSignalPlot = (
    s: Signal,
    key: number,
    model?: DeviceModel
  ): SignalPlot => {
    const signalSlug = _.last(s.slug?.split('.')) ?? '';
    const format = _.get(model, ['signalClasses', signalSlug, 'format'], {
      type: 'precision',
      digits: 5,
    });

    return {
      signal: s,
      rawResolution: temporalResolutionForSamplingFrequency(
        s.samplingFrequency ?? 1
      ),
      styles: {
        line: {
          color: colorPalette.get(key) ?? '#000000',
        },
      },
      visible: true,
      format: (value: number): string => {
        switch (format.type) {
          case 'precision':
            return value.toPrecision(format.digits);

          case 'fixed':
            return value.toFixed(format.digits);

          default:
            return value.toPrecision(5);
        }
      },
    };
  };

  useEffect(() => {
    if (!initializationStarted) {
      setInitializationStarted(true);
      // Load signals
      const originPromises = originRefs
        .map((r) => {
          if (typeof r === 'string') {
            return parseEntityRef(r);
          } else {
            return r;
          }
        })
        .map<Promise<Device | Hardpoint | undefined>>((r) => {
          if (r.entityType === 'device') {
            return illumass.device.read(r).catch((err) => {
              console.error(err);
              return Promise.resolve(undefined);
            });
          } else if (r.entityType === 'hardpoint') {
            return illumass.hardpoint.read(r).catch((err) => {
              console.error(err);
              return Promise.resolve(undefined);
            });
          } else {
            return Promise.resolve(undefined);
          }
        });

      Promise.all(originPromises)
        .then((results) => {
          let originsTemp: List<Device | Hardpoint> = List();
          results.forEach((o) => {
            if (o !== undefined) {
              originsTemp = originsTemp.push(o);
            }
          });

          return originsTemp;
        })
        .then((loadedOrigins) => {
          const originSignalsPromises: List<Promise<List<Signal>>> =
            loadedOrigins.map<Promise<List<Signal>>>(async (d) => {
              if (d.id) {
                const q = _.set(
                  {},
                  ['query', 'term', 'origin.id', 'value'],
                  d.id
                );
                const searchResult = await illumass.signal.search(q, {
                  size: 1000,
                });
                if (searchResult.fetched > 0) {
                  return List(searchResult.hits).map((hit) => hit._source);
                } else {
                  return List();
                }
              } else {
                return List();
              }
            });

          return Promise.all(originSignalsPromises)
            .then((results) => {
              const originSignals: List<List<Signal>> = List(results);
              return loadedOrigins.reduce((reduction, device, key) => {
                const signals = originSignals.get(key);
                if (signals !== undefined && device.id !== undefined) {
                  return reduction.update(device.id, List(), (l) =>
                    l.concat(signals)
                  );
                } else {
                  return reduction;
                }
              }, Map<string, List<Signal>>());
            })
            .then((signalsByOriginId) => {
              // For each origin ref, choose the default signal
              const deviceModelIds: Set<string> = loadedOrigins
                .map((d) => _.get(d, ['deviceModel', 'id']))
                .filter((x) => x !== undefined)
                .toSet();

              const modelPromises = deviceModelIds
                .toList()
                .map((id) => illumass.deviceModel.read('device-model!' + id));

              Promise.all(modelPromises)
                .then((models) =>
                  List(models).reduce(
                    (reduction, model) =>
                      model.id ? reduction.set(model.id, model) : reduction,
                    Map<string, DeviceModel>()
                  )
                )
                .then((modelMap) => {
                  setModels(modelMap);
                  const defaultSignals: List<Signal> = loadedOrigins
                    .map((d) => {
                      const deviceModelId = _.get(d, ['deviceModel', 'id']);
                      if (d.id === undefined || deviceModelId === undefined) {
                        return undefined;
                      }
                      const model = modelMap.get(deviceModelId);
                      if (
                        model === undefined ||
                        model.defaultSignalSlug === undefined
                      ) {
                        return undefined;
                      }
                      const suffix = '.' + model.defaultSignalSlug ?? '';
                      const originSignals =
                        signalsByOriginId.get(d.id) ?? List<Signal>();

                      return originSignals.find((s) =>
                        _.endsWith(s.slug, suffix)
                      );
                    })
                    .filter((s) => s !== undefined) as List<Signal>;
                  const initialSignalPlots = defaultSignals.map((s, key) => {
                    const currentOrigin = loadedOrigins.find(
                      (o) => o.id === (s.origin as EntityRef)?.id
                    );
                    if (
                      currentOrigin?.entityType === 'device' ||
                      currentOrigin?.entityType === 'devices'
                    ) {
                      const currentDeviceModelId = (
                        (currentOrigin as Device)?.deviceModel as EntityRef
                      )?.id;
                      const currentModel =
                        currentDeviceModelId === undefined
                          ? undefined
                          : modelMap.get(currentDeviceModelId);
                      return makeSignalPlot(s, key, currentModel);
                    } else {
                      return makeSignalPlot(s, key);
                    }
                  });

                  setOrigins(loadedOrigins);
                  setSignalPlots(initialSignalPlots);
                });
            });
        });
    }
  }, [initializationStarted, originRefs]);

  useEffect(() => {
    onSignalPlotsChange(signalPlots);
  }, [signalPlots, onSignalPlotsChange]);

  const handleDismissSignal = (itemIndex: number): void => {
    if (signalPlots.size > 1) {
      setSignalPlots(signalPlots.remove(itemIndex));
    } else {
      enqueueSnackbar('The last signal cannot be removed.', {
        variant: 'warning',
      });
    }
  };

  const handleSignalVisibleChange = (
    itemIndex: number,
    visible: boolean
  ): void => {
    const nextSignalPlots = signalPlots.update(itemIndex, (sp) =>
      Object.assign({}, sp, { visible })
    );

    setSignalPlots(nextSignalPlots);
  };

  const handleSignalColorChange = (itemIndex: number, color: string): void => {
    const nextSignalPlots = signalPlots.update(itemIndex, (sp) =>
      Object.assign({}, sp, { styles: { line: { color: color } } })
    );

    setSignalPlots(nextSignalPlots);
  };

  const handleOpenSignalPicker = (): void => {
    const origin = origins.get(0);
    if (origin !== undefined && origin.id !== undefined) {
      setSignalPickerOriginId(origin.id);
    } else {
      enqueueSnackbar('No signal origin found.', {
        variant: 'error',
      });
    }
  };

  const handleCloseSignalPicker = (): void => {
    setSignalPickerOriginId('');
  };

  const handleSignalPicked = (pickedSignal: Signal): void => {
    const pickedOrigin = origins.find(
      (o) => o.id === (pickedSignal.origin as EntityRef)?.id
    );

    let signalPlot: SignalPlot;

    if (
      pickedOrigin?.entityType === 'device' ||
      pickedOrigin?.entityType === 'devices'
    ) {
      const pickedDeviceModelId = (
        (pickedOrigin as Device).deviceModel as EntityRef
      )?.id;
      const pickedModel =
        pickedDeviceModelId === undefined
          ? undefined
          : models.get(pickedDeviceModelId);
      signalPlot = makeSignalPlot(pickedSignal, signalPlots.size, pickedModel);
    } else {
      signalPlot = makeSignalPlot(pickedSignal, signalPlots.size);
    }

    setSignalPlots(signalPlots.push(signalPlot));
    setSignalPickerOriginId('');
  };

  const handleSignalInfoClick = (signal: Signal): void => {
    setInfoSignal(signal);
  };

  const handleSignalInfoClose = (): void => {
    setInfoSignal(undefined);
  };

  return (
    <React.Fragment>
      <Grid container direction="row" justify="flex-start" alignItems="center">
        {signalPlots.map((plot, key) => (
          <Grid key={key} item xs={1}>
            <SignalItem
              itemIndex={key}
              signalPlot={plot}
              cursorValue={props.cursorValues?.get(plot.signal.id ?? '')}
              onDismiss={handleDismissSignal}
              onVisibilityChange={handleSignalVisibleChange}
              onColorChange={handleSignalColorChange}
              onInfoClick={handleSignalInfoClick}
            />
          </Grid>
        ))}
        <Grid item xs={1}>
          <IconButton size="medium" onClick={handleOpenSignalPicker}>
            <Icon fontSize="large">add</Icon>
          </IconButton>
        </Grid>
      </Grid>
      {signalPickerOriginId.length > 0 && (
        <SignalPicker
          open={signalPickerOriginId.length > 0}
          onClose={handleCloseSignalPicker}
          originId={signalPickerOriginId}
          onSignalPicked={handleSignalPicked}
        />
      )}
      <InfoDialog
        onClose={handleSignalInfoClose}
        name={
          ((infoSignal?.origin as EntityRef)?.shortName ?? '') +
          ' ' +
          (infoSignal?.name ?? '')
        }
        idRef={'signal!' + (infoSignal?.id ?? '')}
        slugRef={'signal.' + (infoSignal?.slug ?? '')}
        open={infoSignal !== undefined}
      />
    </React.Fragment>
  );
}
